import clamp from 'lodash/clamp';

import { DEFAULT_CORNER_RADIUS, FREE_CORNER_RADIUS } from '~/core/constants';
import { CommonProductModel } from '~/core/models';
import { Product } from '~/core/utils';

export enum RadiusRangeKeys {
  R0 = 'R0',
  R1 = 'R1',
  R2 = 'R2',
  R3 = 'R3',
  R4 = 'R4',
  R5 = 'R5',
}

export type RadiusRangeValues = Record<'min' | 'max', number>;
type RadiusRangeMap = Record<RadiusRangeKeys, RadiusRangeValues>;
type RadiusRangeString =
  | `[${number},]`
  | `[${number},${number}]`
  | `[,${number}]`;

export interface CornerRadiusModel extends CommonProductModel {
  imageUrl: string;
  priceWithoutDiscount: number;
  range?: RadiusRangeString;
}

export const RADIUS_MAX_DEFAULT_VALUE = 43;
export const RADIUS_FAKE_INFINITY = 5000;

/**
 * These are the valid ranges for radius based on the R[0-5] qteCode
 * The condition is `min < value <= max`
 * @see getCornerRadiusFromSize
 */
export const RADIUS_RANGES: RadiusRangeMap = {
  R0: { min: 0, max: 0.75 },
  R1: { min: 0.75, max: 6 },
  R2: { min: 6, max: 18 },
  R3: { min: 18, max: 30 },
  R4: { min: 30, max: 42 },
  R5: { min: 42, max: RADIUS_FAKE_INFINITY },
};

const EMPTY_RANGE = { min: 0, max: 0 } satisfies RadiusRangeValues;

const hasRangeString = (obj: Product) =>
  'range' in obj && obj.range !== undefined;

const isQteCodeInRange = (qteCode?: string | null) =>
  !!(qteCode && qteCode in RadiusRangeKeys);

export const isCornerRadiusModel = (obj: Product): obj is CornerRadiusModel =>
  isQteCodeInRange(obj.qteCode) || hasRangeString(obj);

const clampRadiusLimit = (base: number) =>
  clamp(base, DEFAULT_CORNER_RADIUS, RADIUS_MAX_DEFAULT_VALUE);

export const parseRangeString = (
  range?: CornerRadiusModel['range']
): RadiusRangeValues => {
  if (!range) {
    return EMPTY_RANGE;
  }
  const [min, max] = range.slice(1, -1).split(',');

  return {
    min: clampRadiusLimit(Number(min || DEFAULT_CORNER_RADIUS)),
    max: clampRadiusLimit(Number(max || RADIUS_MAX_DEFAULT_VALUE)),
  };
};

/**
 * get the min/max range for a given cornerRadiusModel product based on:
 * * qteCode (preferred)
 * * range (old style)
 *
 * otherwise return `{ min: 0, max: 0 }`
 * @param radiusProduct a corner radius product
 */
export const getMinMaxFromCorner = (
  radiusProduct: CornerRadiusModel
): RadiusRangeValues =>
  isQteCodeInRange(radiusProduct.qteCode)
    ? RADIUS_RANGES[radiusProduct.qteCode as RadiusRangeKeys]
    : parseRangeString(radiusProduct.range);

/**
 * Return the product record of the corner radius product associated with a given radius size
 * @param radius the radius of the corner
 * @param cornerRadii the corner radius list
 * @see getCornerRadiusProductIdFromSize
 */
export const getCornerRadiusFromSize = (
  radius: number,
  cornerRadii: CornerRadiusModel[]
): CornerRadiusModel | undefined =>
  cornerRadii.find((cornerRadius) => {
    const { min, max } = getMinMaxFromCorner(cornerRadius);

    // the first check is just to be safe if we get a zero value (should never happen) and have a zero minimum (default radius)
    return (!radius && !min) || (radius > min && radius <= max);
  });

/**
 * Return just the product ID of the corner radius product associated with a given radius size
 * @param radius the radius of the corner
 * @param cornerRadii the corner radius list
 * @see getCornerRadiusFromSize
 */
export const getCornerRadiusProductIdFromSize = (
  radius: number,
  cornerRadii: CornerRadiusModel[]
): number =>
  getCornerRadiusFromSize(radius, cornerRadii)?.productId ??
  FREE_CORNER_RADIUS.ID;
